/**
 * ThemeContext
 * 
 * 管理應用程式的主題模式（深色/淺色）
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getItem, setItem } from '../utils/localStorage';
import { LINE_CONFIG, getLineColor as getBaseLineColor } from '../config/mtrRouteMapLayouts';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'themeMode';
const FONT_SCALE_STORAGE_KEY = 'fontScale';
const DEFAULT_MODE = 'light'; // 默認使用淺色模式
const DEFAULT_FONT_SCALE = 'normal';
const FONT_SCALE_VALUES = {
  small: 15,
  normal: 16,
  large: 17,
};

// 創建主題的函數
const createAppTheme = (mode) => {
  const isDark = mode === 'dark';

  /* 玻璃態配色方案 - 優化暗黑模式對比度 */
  const glassBackground = isDark
    ? 'rgba(15, 23, 42, 0.9)'  // slate-900/90% - 提高不透明度以增強對比
    : 'rgba(255, 255, 255, 0.8)';  // white/80%
  const glassBorder = isDark
    ? 'rgba(255, 255, 255, 0.08)'
    : 'rgba(148, 163, 184, 0.2)';  // slate-300/20%

  return createTheme({
    palette: {
      mode,
      lineColors: Object.keys(LINE_CONFIG).reduce((acc, key) => {
        acc[key] = {
          light: getBaseLineColor(key, 'light'),
          dark: getBaseLineColor(key, 'dark'),
        };
        return acc;
      }, {}),
      primary: {
        main: '#0ea5e9',  // sky-500
        light: '#38bdf8',  // sky-400
        dark: '#0284c7',  // sky-600
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#f97316',  // orange-500
        light: '#fb923c',  // orange-400
        dark: '#ea580c',  // orange-600
        contrastText: '#ffffff',
      },
      error: {
        main: isDark ? '#f87171' : '#ef4444',
        light: '#fca5a5',
        dark: '#b91c1c',
        contrastText: '#ffffff',
      },
      warning: {
        main: isDark ? '#fbbf24' : '#f59e0b',
        light: '#fcd34d',
        dark: '#b45309',
        contrastText: '#0F172A',  // 始終使用深色文字以保證黃色背景上的對比度
      },
      info: {
        main: '#0ea5e9',
        light: '#38bdf8',
        dark: '#0284c7',
        contrastText: '#ffffff',
      },
      success: {
        main: isDark ? '#4ade80' : '#10b981',
        light: '#86efac',
        dark: '#047857',
        contrastText: isDark ? '#0F172A' : '#ffffff',
      },
      background: {
        default: isDark ? '#0f172a' : '#f8fafc',
        paper: glassBackground,
      },
      text: {
        primary: isDark ? 'rgba(248, 250, 252, 0.95)' : '#0F172A',
        secondary: isDark ? 'rgba(248, 250, 252, 0.7)' : '#475569',
        disabled: isDark ? 'rgba(248, 250, 252, 0.4)' : 'rgba(0, 0, 0, 0.38)',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(148, 163, 184, 0.2)',
    },
    typography: {
      fontFamily: '"Noto Sans HK", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: { xs: '2rem', sm: '2.5rem' },
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: { xs: '1.75rem', sm: '2rem' },
        fontWeight: 700,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: { xs: '1.5rem', sm: '1.75rem' },
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: { xs: '1.25rem', sm: '1.5rem' },
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: { xs: '1.125rem', sm: '1.25rem' },
        fontWeight: 500,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: { xs: '0.9375rem', sm: '1rem' },
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: { xs: '0.9375rem', sm: '1rem' },
        lineHeight: 1.5,
      },
      body2: {
        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
        lineHeight: 1.5,
      },
      caption: {
        fontSize: { xs: '0.75rem', sm: '0.75rem' },
        lineHeight: 1.4,
      },
    },
    spacing: 8,
    shape: {
      borderRadius: 12,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            backgroundColor: glassBackground,
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: `1px solid ${glassBorder}`,
            boxShadow: isDark
              ? '0px 8px 32px rgba(0, 0, 0, 0.4)'
              : '0px 8px 32px rgba(0, 0, 0, 0.08)',
            '&:hover': {
              boxShadow: isDark
                ? '0px 12px 48px rgba(0, 0, 0, 0.5)'
                : '0px 12px 48px rgba(0, 0, 0, 0.12)',
              transform: 'translateY(-4px)',
              borderColor: isDark
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(14, 165, 233, 0.3)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: isDark
                ? '0px 4px 12px rgba(0,0,0,0.4)'
                : '0px 4px 12px rgba(14, 165, 233, 0.2)',
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundColor: isDark ? 'rgba(15, 23, 42, 0.8)' : glassBackground,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${glassBorder}`,
            '& .MuiAlert-message': {
              color: isDark ? 'rgba(248, 250, 252, 0.95)' : undefined,
              fontWeight: 500,
            },
          },
          standardSuccess: {
            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : undefined,
            borderColor: isDark ? 'rgba(16, 185, 129, 0.3)' : undefined,
            '& .MuiAlert-icon': { color: isDark ? '#4ade80' : undefined },
          },
          standardError: {
            backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : undefined,
            borderColor: isDark ? 'rgba(239, 68, 68, 0.3)' : undefined,
            '& .MuiAlert-icon': { color: isDark ? '#f87171' : undefined },
          },
          standardWarning: {
            backgroundColor: isDark ? 'rgba(245, 158, 11, 0.15)' : undefined,
            borderColor: isDark ? 'rgba(245, 158, 11, 0.3)' : undefined,
            '& .MuiAlert-icon': { color: isDark ? '#fbbf24' : undefined },
          },
          standardInfo: {
            backgroundColor: isDark ? 'rgba(14, 165, 233, 0.15)' : undefined,
            borderColor: isDark ? 'rgba(14, 165, 233, 0.3)' : undefined,
            '& .MuiAlert-icon': { color: isDark ? '#38bdf8' : undefined },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            backgroundColor: glassBackground,
            backdropFilter: 'blur(20px) saturate(180%)',
            border: `1px solid ${glassBorder}`,
          },
        },
      },
    },
  });
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const savedMode = getItem(STORAGE_KEY);
    if (savedMode === 'light' || savedMode === 'dark') return savedMode;
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    return DEFAULT_MODE;
  });
  const [fontScale, setFontScale] = useState(() => {
    const savedFontScale = getItem(FONT_SCALE_STORAGE_KEY);
    if (savedFontScale === 'small' || savedFontScale === 'normal' || savedFontScale === 'large') {
      return savedFontScale;
    }
    return DEFAULT_FONT_SCALE;
  });

  useEffect(() => {
    document.body.setAttribute('data-theme', mode);
    setItem(STORAGE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    const nextRootFontSize = FONT_SCALE_VALUES[fontScale] || FONT_SCALE_VALUES[DEFAULT_FONT_SCALE];
    document.documentElement.style.fontSize = `${nextRootFontSize}px`;
    setItem(FONT_SCALE_STORAGE_KEY, fontScale);
  }, [fontScale]);

  const toggleMode = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // 提供獲取路線顏色的便捷方法
  const getLineColor = (lineCode) => getBaseLineColor(lineCode, mode);

  const value = {
    mode,
    toggleMode,
    setThemeMode: setMode,
    fontScale,
    setFontScale,
    theme,
    getLineColor,
    lineColors: Object.keys(LINE_CONFIG).reduce((acc, key) => {
      acc[key] = getBaseLineColor(key, mode);
      return acc;
    }, {}),
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};

// 保持向後兼容的導出
export const getLineColor = (lineCode, mode = 'light') => getBaseLineColor(lineCode, mode);
